import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { C } from "../theme";
import useStore from "../store/useStore";
import { supabase } from "../lib/supabase";
import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import {
  LayoutDashboard, Users, CheckSquare, Calendar, FolderOpen, Shield,
  UserCog, Settings, CalendarCheck, BarChart2, LogOut, CalendarDays, ClipboardList, X, PenLine, Briefcase, BookOpen, MoreHorizontal, File, Download, Trash2, Mail, Send, Calculator, Bell, RefreshCw, Clock, ChevronDown, ChevronUp, Play, GraduationCap, CloudOff, Cloud, CloudUpload,
} from "lucide-react";
import QuickEmailCompose from "./QuickEmailCompose";
import { Avatar } from "./ProfilePicture";
import NotificationBell from "./NotificationBell";
import { useTeamMember, useIsTeamMember } from "../context/TeamMemberContext";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { fetchDocuments, uploadDocument, deleteDocument, getDocumentSignedUrl } from "../lib/db";

const Cloey = lazy(() => import("./Cloey"));

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return isMobile;
}

function useIsTablet() {
  const [isTablet, setIsTablet] = useState(() => window.innerWidth > 768 && window.innerWidth <= 1024);
  useEffect(() => {
    const fn = () => setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return isTablet;
}

function usePracticeColor() {
  const [bc, setBc] = useState(() => {
    try { return JSON.parse(localStorage.getItem("taxshop_practice") || "{}").primaryColor || C.orange; }
    catch { return C.orange; }
  });
  useEffect(() => {
    const handler = (e) => setBc(e.detail?.primaryColor || C.orange);
    window.addEventListener("taxshop:practice-updated", handler);
    return () => window.removeEventListener("taxshop:practice-updated", handler);
  }, []);
  return bc;
}

const NAV = [
  { path: "/",           label: "Dashboard",  icon: LayoutDashboard },
  { path: "/clients",    label: "Clients",    icon: Users },
  { path: "/tasks",      label: "Tasks",      icon: CheckSquare },
  { path: "/deadlines",  label: "Deadlines",  icon: Calendar },
  { path: "/planner",    label: "Planner",    icon: CalendarDays },
  { path: "/documents",  label: "Documents",  icon: FolderOpen },
  { path: "/compliance", label: "Compliance", icon: Shield },
  { path: "/bookings",   label: "Bookings",   icon: CalendarCheck },
  { path: "/services",   label: "Services",   icon: Briefcase },
  { path: "/templates",       label: "Templates",       icon: BookOpen },
  { path: "/communications",  label: "Communications",  icon: Mail },
  { path: "/piscore",         label: "PI Score",        icon: BarChart2 },
  { path: "/calculators",        label: "Calculators",    icon: Calculator },
  { path: "/signed-documents",  label: "Signed Docs",    icon: PenLine },
  { path: "/team",              label: "Team",           icon: UserCog },
  { path: "/learning-hub", label: "Learning Hub", icon: GraduationCap },
  { path: "/settings",   label: "Settings",   icon: Settings },
];

function NavTooltip({ label, children }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative", width: "100%" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div style={{
          position: "absolute", left: "calc(100% + 10px)", top: "50%",
          transform: "translateY(-50%)",
          background: "#e9500e", color: "#fff",
          padding: "5px 12px", borderRadius: 8,
          fontSize: 12, fontWeight: 600, whiteSpace: "nowrap",
          pointerEvents: "none", zIndex: 300,
          boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
        }}>
          {label}
          <div style={{
            position: "absolute", right: "100%", top: "50%",
            transform: "translateY(-50%)",
            width: 0, height: 0,
            borderWidth: "5px 5px 5px 0",
            borderStyle: "solid",
            borderColor: "transparent #e9500e transparent transparent",
          }} />
        </div>
      )}
    </div>
  );
}

const BOTTOM_NAV = [
  { path: "/",               label: "Home",       icon: LayoutDashboard },
  { path: "/clients",        label: "Clients",    icon: Users },
  { path: "/tasks",          label: "Tasks",      icon: CheckSquare },
  { path: "/deadlines",      label: "Deadlines",  icon: Calendar },
  { path: "/documents",      label: "Docs",       icon: FolderOpen },
  { path: "/compliance",     label: "Compliance", icon: Shield },
  { path: "/communications", label: "Comms",      icon: Mail },
  { path: "/planner",        label: "Planner",    icon: CalendarDays },
  { path: "/bookings",       label: "Bookings",   icon: CalendarCheck },
  { path: "/services",       label: "Services",   icon: Briefcase },
  { path: "/templates",      label: "Templates",  icon: BookOpen },
  { path: "/piscore",        label: "PI Score",   icon: BarChart2 },
  { path: "/calculators",       label: "Calculators", icon: Calculator },
  { path: "/signed-documents",  label: "Signed Docs", icon: PenLine },
  { path: "/team",              label: "Team",        icon: UserCog },
  { path: "/learning-hub",   label: "Learning",   icon: GraduationCap },
  { path: "/settings",       label: "Settings",   icon: Settings },
];

function fileIcon(name) {
  const n = (name || "").toLowerCase();
  return <File size={18} color="#94A3B8" strokeWidth={1.8} />;
}

function AttachmentSectionInline({ noteId }) {
  const bc = usePracticeColor();
  const [files,     setFiles]     = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const category = `note-${noteId}`;

  useEffect(() => {
    fetchDocuments({ category }).then(setFiles).catch(() => {});
  }, [category]);

  const handleUpload = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(picked.map((f) => uploadDocument(f, category)));
      setFiles((prev) => [...uploaded, ...prev]);
      toast.success(`${picked.length} file${picked.length > 1 ? "s" : ""} attached`);
    } catch (err) {
      toast.error("Upload failed: " + err.message);
    } finally {
      setUploading(false);
      if (e.target) e.target.value = "";
    }
  };

  const openForView = (url, filename) => {
    const ext = (filename || "").split(".").pop().toLowerCase();
    const office = ["docx","doc","xlsx","xls","pptx","ppt"];
    if (office.includes(ext)) {
      window.open(`https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(url)}`, "_blank", "noopener,noreferrer");
    } else {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const handleView = async (doc) => {
    try {
      const url = await getDocumentSignedUrl(doc.filePath, false);
      openForView(url, doc.name);
    } catch { toast.error("Could not open file for viewing."); }
  };

  const handleDownload = async (doc) => {
    try {
      const url = await getDocumentSignedUrl(doc.filePath, true);
      const a = document.createElement("a");
      a.href = url; a.download = doc.name; a.target = "_blank";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch { toast.error("Could not generate download link."); }
  };

  const handleDelete = (doc) => {
    toast((t) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Remove "{doc.name}"?</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={async () => {
            toast.dismiss(t.id);
            try {
              await deleteDocument(doc.id, doc.filePath);
              setFiles((prev) => prev.filter((f) => f.id !== doc.id));
              toast.success("Removed");
            } catch { toast.error("Could not remove."); }
          }} style={{ background: "#EF4444", color: "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
            Delete
          </button>
          <button onClick={() => toast.dismiss(t.id)} style={{ background: "#F1F5F9", color: "#475569", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 8000 });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Upload button */}
      <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={handleUpload}
        accept=".pdf,.xlsx,.xls,.csv,.docx,.doc,.png,.jpg,.jpeg,.gif,.zip,.txt,.pptx,.ppt" />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          background: uploading ? C.slateD : `${bc}0D`,
          border: `2px dashed ${bc}`, borderRadius: 12,
          padding: "14px", fontSize: 14, fontWeight: 700,
          color: bc, cursor: uploading ? "not-allowed" : "pointer",
          opacity: uploading ? 0.7 : 1,
        }}
      >
        {uploading ? "Uploading…" : "📎 Tap to attach files"}
      </button>

      {/* File list */}
      {files.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {files.map((doc) => (
            <div key={doc.id} style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#F8F9FB", borderRadius: 10, padding: "10px 12px",
              border: `1.5px solid rgba(0,140,149,0.12)`,
            }}>
              <span style={{ flexShrink: 0, display: "flex" }}>{fileIcon(doc.name)}</span>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: C.navy, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {doc.name}
              </span>
              <button onClick={() => handleView(doc)}
                style={{ background: C.silverXL, color: C.textSub, border: "none", borderRadius: 8, padding: "6px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
                👁
              </button>
              <button onClick={() => handleDownload(doc)}
                style={{ background: bc, color: "#fff", border: "none", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center" }}>
                <Download size={13} />
              </button>
              <button onClick={() => handleDelete(doc)}
                style={{ background: C.silverXL, color: C.silver, border: "none", borderRadius: 8, padding: "6px 8px", cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center" }}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {files.length === 0 && (
        <div style={{ fontSize: 12, color: C.textSub, textAlign: "center", fontStyle: "italic" }}>
          No files attached yet
        </div>
      )}
    </div>
  );
}

const QUICK_PORTAL_CATS = [
  { id: "general",   label: "General",   color: "#64748B", bg: "#F1F5F9" },
  { id: "tax",       label: "Tax",       color: "#7C3AED", bg: "#F5F3FF" },
  { id: "payroll",   label: "Payroll",   color: "#0369A1", bg: "#EFF6FF" },
  { id: "documents", label: "Documents", color: "#D97706", bg: "#FFFBEB" },
  { id: "urgent",    label: "Urgent",    color: "#DC2626", bg: "#FEF2F2" },
  { id: "resolved",  label: "Resolved",  color: "#16A34A", bg: "#F0FDF4" },
];

function QuickNoteModal({ open, onClose }) {
  const bc = usePracticeColor();
  const addNote  = useStore((s) => s.addNote);
  const clients  = useStore((s) => s.clients);

  // Step 1: form fields
  const [subject,        setSubject]        = useState("");
  const [body,           setBody]           = useState("");
  const [clientId,       setClientId]       = useState("");
  const [saving,         setSaving]         = useState(false);
  const [sendToPortal,   setSendToPortal]   = useState(false);
  const [portalCategory, setPortalCategory] = useState("general");

  // Step 2: saved note id (triggers attachment step)
  const [savedNoteId, setSavedNoteId] = useState(null);

  const subjectRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSubject(""); setBody(""); setClientId("");
      setSaving(false); setSavedNoteId(null);
      setSendToPortal(false); setPortalCategory("general");
      setTimeout(() => subjectRef.current?.focus(), 120);
    }
  }, [open]);

  const handleClientChange = (e) => {
    setClientId(e.target.value);
    if (!e.target.value) { setSendToPortal(false); setPortalCategory("general"); }
  };

  const save = async () => {
    if (!subject.trim()) { toast.error("Subject is required"); return; }
    setSaving(true);
    const client = clients.find((c) => String(c.id) === clientId);
    try {
      const created = await addNote({
        subject:       subject.trim(),
        body:          body.trim(),
        date:          format(new Date(), "yyyy-MM-dd"),
        clientId:      clientId ? Number(clientId) : null,
        client:        client?.name || "",
        status:        "Pending",
        sendToPortal:  sendToPortal && !!clientId,
        portalCategory,
      });
      if (!created) { setSaving(false); return; }
      setSavedNoteId(created.id);
    } catch {
      toast.error("Could not save note");
      setSaving(false);
    }
  };

  if (!open) return null;

  const inp = {
    width: "100%", padding: "11px 14px", border: `1.5px solid ${C.slateD}`,
    borderRadius: 10, fontSize: 15, boxSizing: "border-box",
    background: "#fff", fontFamily: "inherit",
  };

  const SheetHeader = ({ title }) => (
    <>
      <div style={{ width: 36, height: 4, borderRadius: 2, background: C.slateD, margin: "-4px auto 8px" }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 32, height: 32, borderRadius: 10, background: bc, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <PenLine size={16} color="#fff" />
          </div>
          <span style={{ fontSize: 16, fontWeight: 800, color: C.navy }}>{title}</span>
        </div>
        <button onClick={onClose} style={{ background: C.slate, border: "none", borderRadius: 8, padding: "6px 8px", cursor: "pointer", color: C.textSub }}>
          <X size={18} />
        </button>
      </div>
    </>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)" }} />

      {/* ── Step 1: Write note ── */}
      {!savedNoteId && (
        <div style={{
          position: "relative", background: "#fff", borderRadius: "20px 20px 0 0",
          padding: "20px 18px 36px", boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
          display: "flex", flexDirection: "column", gap: 12,
          maxHeight: "90vh", overflowY: "auto",
        }}>
          <SheetHeader title="Quick Note" />

          <input
            ref={subjectRef}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject…"
            style={{ ...inp, fontWeight: 700 }}
          />

          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Details… (optional)"
            rows={4}
            style={{ ...inp, resize: "none", lineHeight: 1.5 }}
          />

          <select
            value={clientId}
            onChange={handleClientChange}
            style={{ ...inp, color: clientId ? C.navy : C.textSub }}
          >
            <option value="">— Link to client (optional) —</option>
            {clients.map((c) => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
          </select>

          {/* Portal toggle — only when a client is selected */}
          {clientId && (
            <div style={{ border: `1.5px solid ${sendToPortal ? "#1AB5A3" : C.slateD}`, borderRadius: 12, overflow: "hidden", transition: "border-color 0.15s" }}>
              <button
                type="button"
                onClick={() => setSendToPortal(v => !v)}
                style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: sendToPortal ? "#F0FDFA" : C.slate, border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
              >
                <div style={{ width: 36, height: 20, borderRadius: 10, background: sendToPortal ? "#1AB5A3" : "#CBD5E1", position: "relative", flexShrink: 0, transition: "background 0.2s" }}>
                  <div style={{ position: "absolute", top: 2, left: sendToPortal ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transition: "left 0.2s" }} />
                </div>
                <Send size={14} color={sendToPortal ? "#1AB5A3" : "#94A3B8"} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: sendToPortal ? "#0F766E" : C.text }}>Send to client portal</div>
                  <div style={{ fontSize: 11, color: C.textSub }}>Client will see this in their portal</div>
                </div>
              </button>
              {sendToPortal && (
                <div style={{ padding: "10px 14px", background: "#fff", borderTop: `1px solid ${C.slateD}` }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 8 }}>Category</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {QUICK_PORTAL_CATS.map(cat => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => setPortalCategory(cat.id)}
                        style={{
                          padding: "4px 12px", borderRadius: 20,
                          border: `1.5px solid ${portalCategory === cat.id ? cat.color : "#E2E8F0"}`,
                          background: portalCategory === cat.id ? cat.bg : "transparent",
                          color: portalCategory === cat.id ? cat.color : "#94A3B8",
                          fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                          transition: "all 0.12s",
                        }}
                      >{cat.label}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            onClick={save}
            disabled={saving}
            style={{
              background: bc, color: "#fff", border: "none", borderRadius: 12,
              padding: "14px", fontSize: 15, fontWeight: 800,
              cursor: saving ? "not-allowed" : "pointer",
              opacity: saving ? 0.7 : 1, marginTop: 4,
            }}
          >
            {saving ? "Saving…" : "Save Note →"}
          </button>
        </div>
      )}

      {/* ── Step 2: Attach files ── */}
      {savedNoteId && (
        <div style={{
          position: "relative", background: "#fff", borderRadius: "20px 20px 0 0",
          padding: "20px 18px 36px", boxShadow: "0 -8px 40px rgba(0,0,0,0.18)",
          display: "flex", flexDirection: "column", gap: 12,
          maxHeight: "90vh", overflowY: "auto",
        }}>
          <SheetHeader title="Attach Files" />

          {/* Success banner */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            background: "#F0FDF4", border: "1.5px solid #6EE7B7",
            borderRadius: 10, padding: "10px 14px",
          }}>
            <span style={{ fontSize: 18 }}>✅</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Note saved!</div>
              <div style={{ fontSize: 12, color: bc }}>"{subject}" — now attach any files below</div>
            </div>
          </div>

          {/* Inline AttachmentSection */}
          {typeof savedNoteId === "number" && (
            <AttachmentSectionInline noteId={savedNoteId} />
          )}

          <button
            onClick={onClose}
            style={{
              background: C.navy, color: "#fff", border: "none", borderRadius: 12,
              padding: "14px", fontSize: 15, fontWeight: 800, cursor: "pointer", marginTop: 4,
            }}
          >
            Done
          </button>
        </div>
      )}
    </div>
  );
}

function PageBanner({ location }) {
  const bc = usePracticeColor();
  const now = new Date();
  const DAYS_L = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const MONTHS_L = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const dayLabel = `${DAYS_L[now.getDay()]}, ${now.getDate()} ${MONTHS_L[now.getMonth()]} ${now.getFullYear()}`;

  if (location.pathname === "/") return null;
  const current = NAV.find(n => n.path !== "/" && location.pathname.startsWith(n.path));
  if (!current) return null;
  const Icon = current.icon;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12,
      marginBottom: 18,
      paddingBottom: 12,
      borderBottom: `2px solid ${bc}20`,
    }}>
      <div style={{ width: 38, height: 38, borderRadius: 12, background: `${bc}12`, border: `1.5px solid ${bc}30`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={18} color={bc} />
      </div>
      <div>
        <div style={{ fontSize: 10, fontWeight: 600, color: C.silver, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 1 }}>{dayLabel}</div>
        <div style={{ fontSize: 19, fontWeight: 800, color: C.navy, letterSpacing: "-0.02em", lineHeight: 1.1 }}>{current.label}</div>
      </div>
    </div>
  );
}

// ── Mobile notification bell button + drop-panel ─────────────────
function MobileBellButton({ open, onToggle, primaryColor }) {
  const bc = primaryColor || "#008C95";
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: msgs } = await (await import("../lib/supabase")).supabase
          .from("portal_messages")
          .select("id, content, attachment_name, created_at, read_by_practice, client_id")
          .eq("from_role", "client")
          .eq("read_by_practice", false)
          .order("created_at", { ascending: false })
          .limit(10);
        const notifs = (msgs || []).map(m => ({
          id: m.id,
          body: (m.content || m.attachment_name || "Sent a message").replace(/<[^>]+>/g, "").slice(0, 70),
          time: m.created_at,
        }));
        setNotifications(notifs);
        setUnread(notifs.length);
      } catch {}
    };
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, []);

  const timeAgo = (ts) => {
    const d = (Date.now() - new Date(ts)) / 1000;
    if (d < 60) return "just now";
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    return `${Math.floor(d / 3600)}h ago`;
  };

  return (
    <div style={{ position: "relative", zIndex: 210 }}>
      {/* Bell button */}
      <button
        onClick={onToggle}
        aria-label="Notifications"
        style={{
          width: 44, height: 44, borderRadius: 10,
          background: open ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.15)",
          border: "1px solid rgba(255,255,255,0.25)",
          color: "#fff", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          position: "relative", flexShrink: 0,
        }}
      >
        <Bell size={20} />
        {unread > 0 && (
          <span style={{
            position: "absolute", top: -4, right: -4,
            minWidth: 16, height: 16, borderRadius: 8,
            background: "#EF4444", color: "#fff",
            fontSize: 9, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: "0 3px", border: "1.5px solid rgba(255,255,255,0.9)",
          }}>
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {/* Drop-panel */}
      {open && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 300,
          paddingTop: "max(env(safe-area-inset-top, 0px), 44px)",
        }}>
          {/* Backdrop */}
          <div onClick={onToggle} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: -1 }} />
          <div style={{
            background: "#fff", borderRadius: "0 0 20px 20px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            maxHeight: "70vh", display: "flex", flexDirection: "column",
          }}>
            {/* Header */}
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "14px 18px 10px",
              borderBottom: "1px solid #F1F5F9",
            }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: "#004A50" }}>
                🔔 Notifications
                {unread > 0 && (
                  <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: "#EF4444" }}>
                    {unread} unread
                  </span>
                )}
              </div>
              <button onClick={onToggle} style={{ background: "none", border: "none", cursor: "pointer", color: "#94A3B8", padding: 4 }}>
                <X size={18} />
              </button>
            </div>

            {/* Notification list */}
            <div style={{ overflowY: "auto", flex: 1 }}>
              {notifications.length === 0 ? (
                <div style={{ padding: "36px 18px", textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 10 }}>🎉</div>
                  <div style={{ fontWeight: 700, color: "#004A50", fontSize: 14 }}>All caught up!</div>
                  <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 4 }}>No unread messages</div>
                </div>
              ) : (
                notifications.map(n => (
                  <button key={n.id} onClick={() => { onToggle(); navigate("/clients"); }}
                    style={{
                      width: "100%", textAlign: "left", border: "none",
                      background: "#EFF6FF", borderBottom: "1px solid #F1F5F9",
                      padding: "13px 18px", cursor: "pointer", display: "flex", gap: 12, alignItems: "flex-start",
                      fontFamily: "inherit",
                    }}
                  >
                    <div style={{
                      width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                      background: "#DBEAFE", display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Mail size={16} color="#3B82F6" />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#004A50" }}>
                        New client message
                        <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: "#3B82F6", marginLeft: 6, verticalAlign: "middle" }} />
                      </div>
                      <div style={{ fontSize: 12, color: "#64748B", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {n.body}
                      </div>
                      <div style={{ fontSize: 10, color: "#94A3B8", marginTop: 3 }}>{timeAgo(n.time)}</div>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div style={{ padding: "10px 18px", borderTop: "1px solid #F1F5F9", textAlign: "center" }}>
                <button onClick={() => { onToggle(); navigate("/notifications"); }}
                  style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: bc, fontWeight: 700, fontFamily: "inherit" }}>
                  View all activity →
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Paths accessible to team members (Clients list + Planner only)
const TEAM_MEMBER_PATHS = ["/clients", "/planner"];

function MobileLayout({ children, location }) {
  const bc = usePracticeColor();
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  const [bellOpen,      setBellOpen]      = useState(false);
  const navigate = useNavigate();
  const notes = useStore(s => s.notes);
  const pendingNotesCount = notes.filter(n => !n.clientId && n.status === "Pending").length;
  const isTeamMember = useIsTeamMember();

  const allNavItems = isTeamMember
    ? NAV.filter(n => TEAM_MEMBER_PATHS.includes(n.path))
    : NAV;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", fontFamily: "'Open Sans','Segoe UI',sans-serif", background: "#FFFFFF", color: C.text }}>

      {/* Mobile top bar — paddingTop pushes below iPhone status bar */}
      <div style={{
        position: "sticky", top: 0, zIndex: 100,
        background: `linear-gradient(135deg, ${bc}EE 0%, ${bc} 100%)`,
        paddingTop: "max(env(safe-area-inset-top, 0px), 44px)",
        paddingBottom: 6,
        boxShadow: `0 3px 16px ${bc}72`,
      }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", height: 64, padding: "0 14px", boxSizing: "border-box" }}>
          {/* Notification bell */}
          <MobileBellButton open={bellOpen} onToggle={() => setBellOpen(v => !v)} primaryColor={bc} />
          {/* Logo + title centred */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, flex: 1 }}>
            <img src="/mdg-logo-white.png" alt="MDG Accounting" style={{ width: 44, height: 44, objectFit: "contain" }} />
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#fff", fontWeight: 800, fontSize: 13, letterSpacing: "-0.01em", lineHeight: 1 }}>MDG Accounting</div>
              <div style={{ color: "rgba(255,255,255,0.75)", fontWeight: 600, fontSize: 9, letterSpacing: "0.1em", textTransform: "uppercase" }}>Business Made Easier</div>
            </div>
          </div>
          {/* Menu button */}
          <button
            onClick={() => setDrawerOpen(true)}
            aria-label="Open navigation menu"
            style={{
              background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 10,
              padding: "0", cursor: "pointer", color: "#fff", display: "flex", alignItems: "center",
              width: 44, height: 44, justifyContent: "center", flexShrink: 0,
            }}
          >
            <MoreHorizontal size={22} />
          </button>
        </div>
      </div>

      {/* Drawer overlay */}
      {drawerOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200 }}>
          <div onClick={() => setDrawerOpen(false)} style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />
          <div style={{
            position: "absolute", right: 0, top: 0, bottom: 0, width: 260,
            background: `linear-gradient(180deg, ${bc} 0%, ${bc}CC 100%)`, display: "flex", flexDirection: "column",
            boxShadow: "-4px 0 32px rgba(0,140,149,0.45)",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 16px 8px" }}>
              <span style={{ color: "#fff", fontWeight: 800, fontSize: 14 }}>Menu</span>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close menu" style={{ background: "none", border: "none", color: "#fff", cursor: "pointer" }}>
                <X size={20} />
              </button>
            </div>
            <nav style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
              {allNavItems.map(({ path, label, icon: Icon }) => {
                const active = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
                return (
                  <button key={path} onClick={() => { navigate(path); setDrawerOpen(false); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 12, width: "100%",
                      padding: "12px 12px", minHeight: 44, borderRadius: 10, marginBottom: 3,
                      background: active ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.12)",
                      color: active ? "#D0EFEF" : "rgba(255,255,255,0.9)",
                      fontWeight: active ? 700 : 500, fontSize: 14,
                      border: active ? "1px solid rgba(208,239,239,0.4)" : "1px solid transparent",
                      cursor: "pointer", textAlign: "left",
                    }}>
                    <Icon size={18} />
                    {label}
                  </button>
                );
              })}
            </nav>
            <div style={{ padding: "12px 16px", borderTop: "1px solid rgba(255,255,255,0.15)", display: "flex", flexDirection: "column", gap: 4 }}>
              <button onClick={() => { useStore.getState().resetStore(); supabase.auth.signOut(); }} style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                background: "none", border: "none", color: "rgba(255,255,255,0.7)",
                fontSize: 13, fontWeight: 600, cursor: "pointer", padding: "8px 4px", fontFamily: "inherit",
              }}>
                <LogOut size={16} /> Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content — overflowX must NOT be "hidden": that blocks inner horizontal scroll containers on iOS Safari */}
      <div id="main-scroll-container" style={{ flex: 1, overflowY: "auto", paddingBottom: 96, background: "#fff" }}>
        <div style={{ padding: "14px 12px", width: "100%", boxSizing: "border-box", minWidth: 0, maxWidth: "100vw" }}>
          <PageBanner location={location} />
          {children}
        </div>
      </div>

      {/* Quick Note FAB */}
      <button
        onClick={() => setQuickNoteOpen(true)}
        aria-label="Quick note"
        style={{
          position: "fixed", right: 18,
          bottom: "calc(76px + env(safe-area-inset-bottom, 0px))",
          zIndex: 90,
          width: 52, height: 52, borderRadius: "50%",
          background: bc, border: "none", cursor: "pointer",
          boxShadow: `0 4px 20px ${bc}8C`,
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "transform 0.15s",
        }}
        onTouchStart={(e) => e.currentTarget.style.transform = "scale(0.92)"}
        onTouchEnd={(e) => e.currentTarget.style.transform = "scale(1)"}
      >
        <PenLine size={22} color="#fff" />
      </button>

      <QuickNoteModal open={quickNoteOpen} onClose={() => setQuickNoteOpen(false)} />

      {/* Bottom nav bar — scrollable strip showing all pages */}
      <div className="bottom-nav" style={{
        position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 100,
        background: bc,
        boxShadow: "0 -2px 20px rgba(0,140,149,0.4)",
        height: 82,
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        display: "flex",
        alignItems: "stretch",
      }}>
        {BOTTOM_NAV.map(({ path, label, icon: Icon }) => {
          const active = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
          const isComms = path === "/communications";
          return (
            <NavLink key={path} to={path} style={{
              flex: "0 0 auto",
              minWidth: 76, width: 76,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              gap: 5, textDecoration: "none",
              color: active ? "#D0EFEF" : "rgba(255,255,255,0.65)",
              fontSize: 11, fontWeight: active ? 700 : 500,
              borderTop: active ? "3px solid #D0EFEF" : "3px solid transparent",
              transition: "all 0.15s",
              padding: "0 4px",
              position: "relative",
            }}>
              <div style={{ position: "relative" }}>
                <Icon size={22} />
                {isComms && pendingNotesCount > 0 && (
                  <span style={{ position: "absolute", top: -5, right: -8, background: "#EF4444", color: "#fff", borderRadius: "50%", minWidth: 16, height: 16, fontSize: 9, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid rgba(0,140,149,0.8)", padding: "0 2px" }}>
                    {pendingNotesCount > 9 ? "9+" : pendingNotesCount}
                  </span>
                )}
              </div>
              <span style={{ textAlign: "center", lineHeight: 1.1, whiteSpace: "nowrap" }}>{label}</span>
            </NavLink>
          );
        })}
      </div>

      {!isTeamMember && <Suspense fallback={null}><Cloey /></Suspense>}
    </div>
  );
}

const INACTIVITY_MS = 5 * 60 * 1000;

function FloatingTimerWidget() {
  const bc = usePracticeColor();
  const activeTimer = useStore(s => s.activeTimer);
  const startTimer = useStore(s => s.startTimer);
  const stopTimer = useStore(s => s.stopTimer);
  const clients = useStore(s => s.clients);
  const team = useStore(s => s.team);

  const [expanded, setExpanded] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [description, setDescription] = useState("");
  const [isBillable, setIsBillable] = useState(true);
  const [selectedUser, setSelectedUser] = useState(() => team?.[0]?.name || "Monique");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);

  // Auto-expand when timer starts
  const prevTimerRef = useRef(null);
  useEffect(() => {
    if (!prevTimerRef.current && activeTimer) { setExpanded(true); setDescription(""); setIsBillable(true); }
    prevTimerRef.current = activeTimer;
  }, [activeTimer]);

  // Allow header button to open Track Time
  useEffect(() => {
    const handler = () => setExpanded(true);
    window.addEventListener("track-time:open", handler);
    return () => window.removeEventListener("track-time:open", handler);
  }, []);

  // Sync team default user
  useEffect(() => {
    if (team?.length && !selectedUser) setSelectedUser(team[0].name);
  }, [team, selectedUser]);

  // Tick
  useEffect(() => {
    if (!activeTimer?.startAt) { setElapsed(0); return; }
    const tick = () => setElapsed(Math.floor((Date.now() - new Date(activeTimer.startAt)) / 1000));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeTimer?.startAt]);

  // Inactivity auto-stop
  const inactivityRef = useRef(null);
  const resetInactivity = useCallback(() => {
    clearTimeout(inactivityRef.current);
    if (!useStore.getState().activeTimer) return;
    inactivityRef.current = setTimeout(async () => {
      const still = useStore.getState().activeTimer;
      if (still) {
        await useStore.getState().stopTimer({ description: "", isBillable: true });
        import("react-hot-toast").then(({ default: t }) =>
          t("Timer auto-stopped — 5 min inactivity", { icon: "⏱️", duration: 5000 })
        );
      }
    }, INACTIVITY_MS);
  }, []);

  useEffect(() => {
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach(e => window.addEventListener(e, resetInactivity, { passive: true }));
    resetInactivity();
    return () => { events.forEach(e => window.removeEventListener(e, resetInactivity)); clearTimeout(inactivityRef.current); };
  }, [resetInactivity]);

  const h = Math.floor(elapsed / 3600);
  const m = Math.floor((elapsed % 3600) / 60);
  const s = elapsed % 60;
  const display = h > 0
    ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
    : `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;

  const handleStop = async () => {
    await stopTimer({ description, isBillable });
    setExpanded(false);
    setDescription("");
    import("react-hot-toast").then(({ default: t }) => t.success("Session saved to timesheet"));
  };

  const handleStart = () => {
    const client = clients.find(cl => cl.id === selectedClientId);
    if (!client) { import("react-hot-toast").then(({ default: t }) => t.error("Select a client first")); return; }
    startTimer(client.id, client.name, selectedUser || team?.[0]?.name || "Monique");
    import("react-hot-toast").then(({ default: t }) => t.success(`Timer started — ${client.name}`));
  };

  const filteredClients = (clients || []).filter(cl =>
    !clientSearch || cl.name.toLowerCase().includes(clientSearch.toLowerCase())
  ).slice(0, 8);

  const FONT = "'Open Sans','Segoe UI',sans-serif";

  const isMobileTimer = window.innerWidth < 768;

  // ── No timer running — desktop shows button in header, mobile shows FAB ──
  if (!activeTimer && !expanded) {
    if (!isMobileTimer) return null;
    return (
      <div style={{ position: "fixed", bottom: 88, right: 24, zIndex: 9990, fontFamily: FONT }}>
        <button onClick={() => setExpanded(true)} title="Track time"
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "10px 18px", borderRadius: 50,
            background: bc, color: "#fff", border: "none", cursor: "pointer",
            fontWeight: 700, fontSize: 12, fontFamily: FONT,
            boxShadow: `0 4px 20px ${bc}50, 0 2px 8px rgba(0,0,0,0.15)`,
            transition: "all 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.06)"; e.currentTarget.style.boxShadow = `0 6px 28px ${bc}60`; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = `0 4px 20px ${bc}50, 0 2px 8px rgba(0,0,0,0.15)`; }}
        >
          <Clock size={13} /> Track Time
        </button>
      </div>
    );
  }

  // ── Start panel (no timer, expanded) ──
  if (!activeTimer && expanded) {
    return (
      <div style={{
        position: "fixed", bottom: 88, right: 24, zIndex: 9990,
        width: 300, fontFamily: FONT,
        background: "#fff", border: "1.5px solid #E5E7EB",
        borderRadius: 16, boxShadow: "0 8px 40px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}>
        <div style={{ background: bc, padding: "13px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Clock size={14} color="#fff" />
            <span style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>Track Time</span>
          </div>
          <button onClick={() => setExpanded(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.8)", display: "flex", padding: 2 }}>
            <X size={14} />
          </button>
        </div>
        <div style={{ padding: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 10, fontWeight: 800, color: "#9CA3AF", letterSpacing: "0.1em", textTransform: "uppercase", display: "block", marginBottom: 5 }}>Client</label>
            <div style={{ position: "relative" }}>
              <input
                placeholder="Search clients…"
                value={clientSearch}
                onChange={e => { setClientSearch(e.target.value); setShowDropdown(true); setSelectedClientId(""); }}
                onFocus={() => setShowDropdown(true)}
                style={{ width: "100%", padding: "8px 10px", border: "1.5px solid #E5E7EB", borderRadius: 8, fontSize: 12, fontFamily: FONT, outline: "none", boxSizing: "border-box", color: "#005F63" }}
              />
              {showDropdown && filteredClients.length > 0 && (
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10, border: "1px solid #E5E7EB", borderRadius: 8, marginTop: 3, background: "#fff", maxHeight: 180, overflowY: "auto", boxShadow: "0 4px 16px rgba(0,0,0,0.1)" }}>
                  {filteredClients.map(cl => (
                    <div key={cl.id}
                      onMouseDown={() => { setSelectedClientId(cl.id); setClientSearch(cl.name); setShowDropdown(false); }}
                      style={{
                        padding: "8px 10px", fontSize: 12, cursor: "pointer",
                        background: selectedClientId === cl.id ? `${bc}12` : "#fff",
                        color: selectedClientId === cl.id ? bc : "#005F63",
                        fontWeight: selectedClientId === cl.id ? 700 : 400,
                        borderBottom: "1px solid #F3F4F6",
                      }}
                      onMouseEnter={e => { if (selectedClientId !== cl.id) e.currentTarget.style.background = "#F9FAFB"; }}
                      onMouseLeave={e => { if (selectedClientId !== cl.id) e.currentTarget.style.background = "#fff"; }}
                    >{cl.name}</div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 10, fontWeight: 800, color: "#9CA3AF", letterSpacing: "0.1em", textTransform: "uppercase", display: "block", marginBottom: 5 }}>Team Member</label>
            <select value={selectedUser} onChange={e => setSelectedUser(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", border: "1.5px solid #E5E7EB", borderRadius: 8, fontSize: 12, fontFamily: FONT, outline: "none", color: "#005F63" }}>
              {(team || []).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
            </select>
          </div>
          <button onClick={handleStart}
            style={{
              width: "100%", padding: "11px", borderRadius: 10, border: "none",
              background: selectedClientId ? bc : "#E5E7EB",
              color: selectedClientId ? "#fff" : "#9CA3AF",
              fontWeight: 800, fontSize: 13, cursor: selectedClientId ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              fontFamily: FONT, transition: "all 0.2s",
              boxShadow: selectedClientId ? `0 4px 16px ${bc}40` : "none",
            }}
          >
            <Play size={12} fill="currentColor" /> Start Timer
          </button>
        </div>
      </div>
    );
  }

  // ── Timer IS running ──
  return (
    <div style={{ position: "fixed", bottom: 88, right: 24, zIndex: 9990, fontFamily: FONT }}>
      {expanded ? (
        <div style={{
          width: 300,
          background: "#fff", border: `1.5px solid ${bc}`,
          borderRadius: 16,
          boxShadow: `0 8px 40px ${bc}25, 0 2px 12px rgba(0,0,0,0.1)`,
          overflow: "hidden",
        }}>
          {/* Card header */}
          <div style={{ background: bc, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", animation: "ftwpulse 1.5s infinite", flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", letterSpacing: "0.1em", textTransform: "uppercase" }}>Recording</span>
            </div>
            <button onClick={() => setExpanded(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.85)", display: "flex", padding: 2 }}>
              <ChevronDown size={14} />
            </button>
          </div>
          {/* Card body */}
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", marginBottom: 2 }}>{activeTimer.clientName}</div>
            <div style={{ fontSize: 46, fontWeight: 900, color: bc, fontFamily: "monospace", letterSpacing: "-2px", lineHeight: 1.05, marginBottom: 4, fontVariantNumeric: "tabular-nums" }}>
              {display}
            </div>
            <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 14 }}>
              {activeTimer.userName} · started {new Date(activeTimer.startAt).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
            </div>
            <input
              placeholder="What are you working on? (optional)"
              value={description}
              onChange={e => setDescription(e.target.value)}
              style={{ width: "100%", padding: "8px 10px", border: "1.5px solid #E5E7EB", borderRadius: 8, fontSize: 11, fontFamily: FONT, outline: "none", color: "#005F63", boxSizing: "border-box", marginBottom: 10 }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
              <button onClick={() => setIsBillable(b => !b)} style={{
                width: 36, height: 20, borderRadius: 10, border: "none", cursor: "pointer",
                background: isBillable ? "#10B981" : "#D1D5DB",
                position: "relative", transition: "background 0.2s", flexShrink: 0,
              }}>
                <div style={{ position: "absolute", top: 2, left: isBillable ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
              </button>
              <span style={{ fontSize: 11, fontWeight: 600, color: isBillable ? "#10B981" : "#9CA3AF" }}>
                {isBillable ? "Billable" : "Non-billable"}
              </span>
            </div>
            <button onClick={handleStop}
              style={{
                width: "100%", padding: "12px", borderRadius: 10, border: "none",
                background: "#EF4444", color: "#fff", fontWeight: 800, fontSize: 13,
                cursor: "pointer", fontFamily: FONT, transition: "all 0.15s",
                boxShadow: "0 4px 16px rgba(239,68,68,0.35)",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "#DC2626"; e.currentTarget.style.transform = "scale(1.01)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "#EF4444"; e.currentTarget.style.transform = ""; }}
            >
              <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor"><rect x="2" y="2" width="12" height="12" rx="2"/></svg>
              Stop & Save
            </button>
          </div>
        </div>
      ) : (
        /* Collapsed pill */
        <div
          onClick={() => setExpanded(true)}
          style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "10px 16px", borderRadius: 50,
            background: bc, cursor: "pointer",
            boxShadow: `0 4px 20px ${bc}50, 0 2px 8px rgba(0,0,0,0.15)`,
            transition: "all 0.2s",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.04)"; e.currentTarget.style.boxShadow = `0 6px 28px ${bc}60`; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = `0 4px 20px ${bc}50, 0 2px 8px rgba(0,0,0,0.15)`; }}
        >
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#fff", animation: "ftwpulse 1.5s infinite", flexShrink: 0 }} />
          <span style={{ fontSize: 15, fontWeight: 900, color: "#fff", fontFamily: "monospace", letterSpacing: "0.02em", fontVariantNumeric: "tabular-nums" }}>
            {display}
          </span>
          <span style={{ fontSize: 10, color: "rgba(255,255,255,0.85)", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {activeTimer.clientName}
          </span>
          <ChevronUp size={12} color="rgba(255,255,255,0.8)" />
        </div>
      )}
      <style>{`@keyframes ftwpulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.7)} }`}</style>
    </div>
  );
}

function CloudSyncBadge() {
  const [status, setStatus] = useState("idle"); // idle | saving | saved | error
  const [errorMsg, setErrorMsg] = useState("");
  const [retrying, setRetrying] = useState(false);
  const savedTimer = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const { status: s, reason } = e.detail || {};
      if (s === "saving") {
        setStatus("saving");
        if (savedTimer.current) clearTimeout(savedTimer.current);
      } else if (s === "saved") {
        setStatus("saved");
        setErrorMsg("");
        savedTimer.current = setTimeout(() => setStatus("idle"), 3000);
      } else if (s === "error") {
        setStatus("error");
        setErrorMsg(reason || "Cloud save failed. Data is safe locally.");
      }
    };
    window.addEventListener("taxshop:wp-sync", handler);
    return () => window.removeEventListener("taxshop:wp-sync", handler);
  }, []);

  const retry = async () => {
    setRetrying(true);
    try { await window.retryWorkpaperSave?.(); } catch {}
    setRetrying(false);
  };

  if (status === "idle") return null;

  if (status === "saving") return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#6B7280", padding: "4px 10px", borderRadius: 7, border: "1px solid #E5E7EB", background: "#F9FAFB" }}>
      <CloudUpload size={13} style={{ animation: "spin 1.2s linear infinite" }} /> Saving…
    </div>
  );

  if (status === "saved") return (
    <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: "#15803D", padding: "4px 10px", borderRadius: 7, border: "1px solid #BBF7D0", background: "#F0FDF4" }}>
      <Cloud size={13} /> Saved
    </div>
  );

  if (status === "error") return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, padding: "4px 10px", borderRadius: 7, border: "1px solid #FECACA", background: "#FFF1F2", maxWidth: 320 }}>
      <CloudOff size={13} style={{ color: "#DC2626", flexShrink: 0 }} />
      <span style={{ color: "#B91C1C", flex: 1 }}>{errorMsg}</span>
      <button onClick={retry} disabled={retrying} style={{ background: "#DC2626", color: "#fff", border: "none", borderRadius: 5, padding: "2px 8px", fontSize: 10, fontWeight: 700, cursor: "pointer", flexShrink: 0 }}>
        {retrying ? "…" : "Retry"}
      </button>
    </div>
  );

  return null;
}

function DesktopTopbar({ userEmail }) {
  const bc = usePracticeColor();
  const initials = userEmail ? userEmail[0].toUpperCase() : "M";
  const now = new Date();
  const [composeOpen, setComposeOpen] = useState(false);
  const isTeamMember = useIsTeamMember();
  const teamMember = useTeamMember();
  // Team member display: use their name initials and name
  const tmInitials = teamMember?.name ? teamMember.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) : "TM";
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";


  return (<>
    <div style={{
      position: "sticky", top: 0, zIndex: 50,
      height: 58, background: "#ffffff",
      borderBottom: "1px solid #E5E7EB",
      display: "flex", alignItems: "center",
      padding: "0 24px", gap: 16,
      boxShadow: "0 1px 0 #E5E7EB",
    }}>
      <div style={{ flex: 1, minWidth: 0 }} />

      <CloudSyncBadge />

      {/* Compose email icon — owner only */}
      {!isTeamMember && <button
        onClick={() => setComposeOpen(true)}
        title="Compose email"
        style={{
          display: "flex", alignItems: "center", gap: 5,
          padding: "5px 11px", borderRadius: 8,
          border: "1px solid #C5C7C9", background: "#fff",
          color: C.silver, fontSize: 12, fontWeight: 500, cursor: "pointer",
          transition: "all 0.15s", fontFamily: "inherit",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${bc}15`; e.currentTarget.style.color = bc; e.currentTarget.style.borderColor = `${bc}60`; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#6B7280"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
      >
        <Mail size={13} /> Compose
      </button>}

      {/* Cloey AI assistant — owner only */}
      {!isTeamMember && <button
        onClick={() => window.dispatchEvent(new CustomEvent("cloey:open"))}
        title="Chat with Cloey"
        style={{
          display: "flex", alignItems: "center", gap: 6,
          padding: "4px 10px 4px 5px", borderRadius: 8,
          border: `1.5px solid ${C.orange}20`, background: `${C.orange}10`,
          color: C.orange, fontSize: 12, fontWeight: 700, cursor: "pointer",
          transition: "all 0.15s", fontFamily: "inherit",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${C.orange}22`; e.currentTarget.style.borderColor = C.orange; }}
        onMouseLeave={e => { e.currentTarget.style.background = `${C.orange}10`; e.currentTarget.style.borderColor = `${C.orange}20`; }}
      >
        <img src="/cloey-head.png" alt="Cloey" style={{ width: 24, height: 24, objectFit: "cover", borderRadius: 6 }} />
        Cloey
      </button>}

      {/* Track Time — owner only */}
      {!isTeamMember && <button
        onClick={() => window.dispatchEvent(new CustomEvent("track-time:open"))}
        title="Track Time"
        style={{
          display: "flex", alignItems: "center", gap: 5,
          padding: "5px 11px", borderRadius: 8,
          border: `1px solid ${bc}30`, background: `${bc}10`,
          color: bc, fontSize: 12, fontWeight: 600, cursor: "pointer",
          transition: "all 0.15s", fontFamily: "inherit",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = `${bc}22`; e.currentTarget.style.borderColor = bc; }}
        onMouseLeave={e => { e.currentTarget.style.background = `${bc}10`; e.currentTarget.style.borderColor = `${bc}30`; }}
      >
        <Clock size={13} /> Track Time
      </button>}

      {/* WhatsApp link — owner only */}
      {!isTeamMember && <a
        href="https://api.whatsapp.com/send/?phone=27715549421&text&type=phone_number&app_absent=0"
        target="_blank" rel="noopener noreferrer"
        title="WhatsApp"
        style={{
          display: "flex", alignItems: "center", gap: 5,
          padding: "5px 11px", borderRadius: 8,
          border: "1px solid #C5C7C9", textDecoration: "none",
          color: C.silver, fontSize: 12, fontWeight: 500,
          transition: "all 0.15s", background: "#fff",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#F0FDF4"; e.currentTarget.style.color = "#15803d"; e.currentTarget.style.borderColor = "#86EFAC"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#6B7280"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
      >
        <svg viewBox="0 0 32 32" width="12" height="12" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
          <path fill="currentColor" d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.47 2.027 7.773L0 32l8.437-2.007A15.93 15.93 0 0016 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 01-6.763-1.843l-.485-.288-5.007 1.192 1.258-4.878-.317-.5A13.245 13.245 0 012.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333z"/>
          <path fill="currentColor" d="M23.28 19.488c-.368-.184-2.174-1.073-2.511-1.195-.337-.122-.582-.184-.827.184-.245.368-.949 1.195-1.163 1.44-.214.245-.428.276-.796.092-.368-.184-1.553-.572-2.957-1.824-1.093-.975-1.83-2.179-2.044-2.547-.214-.368-.023-.567.161-.75.165-.165.368-.428.552-.643.184-.215.245-.368.368-.613.122-.245.061-.46-.031-.643-.092-.184-.827-1.993-1.133-2.731-.299-.717-.602-.62-.827-.631l-.705-.012c-.245 0-.643.092-.98.46-.337.368-1.287 1.258-1.287 3.067s1.318 3.558 1.502 3.803c.184.245 2.594 3.96 6.288 5.554.879.38 1.565.606 2.1.776.882.281 1.685.241 2.319.146.707-.105 2.174-.888 2.48-1.747.307-.858.307-1.594.215-1.747-.092-.153-.337-.245-.705-.429z"/>
        </svg>
        WhatsApp
      </a>}

      {/* Notification bell — owner only */}
      {!isTeamMember && <NotificationBell primaryColor={bc} />}

      {/* Team member greeting banner in header */}
      {isTeamMember && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 14px", background: `${bc}12`, border: `1px solid ${bc}30`, borderRadius: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: bc }}>{greeting}, {teamMember?.name?.split(" ")[0] || "there"} 👋</span>
        </div>
      )}

      {/* Avatar + refresh + sign out */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {isTeamMember
          ? <div style={{ width: 30, height: 30, borderRadius: 8, background: bc, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800 }}>{tmInitials}</div>
          : <Avatar name="Monique" initials={initials} color="#FBBF24" size={30} radius={8} fontSize={12} />
        }
        <button onClick={() => window.location.reload()} title="Refresh (Ctrl+Shift+R)" style={{
          background: "none", border: "1px solid #C5C7C9", borderRadius: 7,
          color: C.silver, cursor: "pointer", padding: "5px 10px",
          fontSize: 12, display: "flex", alignItems: "center", gap: 5,
          transition: "all 0.15s", fontFamily: "inherit",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.color = "#2563EB"; e.currentTarget.style.borderColor = "#BFDBFE"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#6B7280"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
        >
          <RefreshCw size={12} /> Refresh
        </button>
        <button onClick={() => { useStore.getState().resetStore(); supabase.auth.signOut(); }} title="Sign out" style={{
          background: "none", border: "1px solid #C5C7C9", borderRadius: 7,
          color: C.silver, cursor: "pointer", padding: "5px 10px",
          fontSize: 12, display: "flex", alignItems: "center", gap: 5,
          transition: "all 0.15s", fontFamily: "inherit",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "#FEF2F2"; e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.borderColor = "#FECACA"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "none"; e.currentTarget.style.color = "#6B7280"; e.currentTarget.style.borderColor = "#E5E7EB"; }}
        >
          <LogOut size={12} /> Sign out
        </button>
      </div>
    </div>
    <QuickEmailCompose open={composeOpen} onClose={() => setComposeOpen(false)} />
  </>);
}

export default function Layout({ children }) {
  const bc = usePracticeColor();
  const location = useLocation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isTeamMember = useIsTeamMember();
  const [userEmail,   setUserEmail]   = useState("");
  const notes = useStore(s => s.notes);
  const pendingNotesCount = notes.filter(n => !n.clientId && n.status === "Pending").length;
  const visibleNav = isTeamMember ? NAV.filter(n => TEAM_MEMBER_PATHS.includes(n.path)) : NAV;
  const [collapsed,   setCollapsed]   = useState(() => {
    try { return localStorage.getItem("taxshop_nav_collapsed") === "1"; } catch { return false; }
  });

  // Reset scroll position on every route change
  useEffect(() => {
    const el = document.getElementById("main-scroll");
    if (el) el.scrollTop = 0;
    window.scrollTo({ top: 0, behavior: "instant" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname]);

  // Auto-collapse sidebar on tablet range
  useEffect(() => {
    if (isTablet) setCollapsed(true);
  }, [isTablet]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setUserEmail(data?.user?.email || "");
    });
  }, []);

  if (isMobile) return <MobileLayout location={location}>{children}</MobileLayout>;

  const W = collapsed ? 60 : 210;

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", fontFamily: "'Open Sans','Segoe UI',sans-serif", background: "#FFFFFF", color: C.text }}>

      {/* ── Sidebar ── */}
      <div style={{
        width: W, flexShrink: 0,
        background: "#ffffff",
        borderRight: "1px solid #E5E7EB",
        display: "flex", flexDirection: "column",
        height: "100vh", position: "fixed", top: 0, left: 0, zIndex: 50,
        overflowY: "auto", overflowX: "hidden",
        transition: "width 0.22s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: "2px 0 20px rgba(0,0,0,0.06)",
      }}>
        {/* Logo + collapse toggle — white header section */}
        <div style={{
          padding: collapsed ? "14px 0" : "12px 14px",
          background: "#fff",
          borderBottom: "1px solid #E5E7EB",
          display: "flex", alignItems: "center", gap: 8,
          justifyContent: collapsed ? "center" : "space-between",
          flexShrink: 0,
        }}>
          {!collapsed && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              <img src="/mdg-logo-transparent.png" alt="MDG Accounting" style={{ width: 34, height: 34, objectFit: "contain", flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "#20272B", fontWeight: 800, fontSize: 12.5, lineHeight: 1.2, whiteSpace: "nowrap" }}>MDG Accounting</div>
                <div style={{ color: bc, fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginTop: 2 }}>Business Made Easier</div>
              </div>
            </div>
          )}
          {collapsed && (
            <img src="/mdg-logo-transparent.png" alt="MDG Accounting" style={{ width: 34, height: 34, objectFit: "contain" }} />
          )}
          <button onClick={() => setCollapsed(v => { const next = !v; try { localStorage.setItem("taxshop_nav_collapsed", next ? "1" : "0"); } catch {} return next; })}
            title={collapsed ? "Expand menu" : "Collapse menu"}
            style={{ background: "none", border: "none", color: "#B0B8C1", cursor: "pointer", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "color 0.15s", borderRadius: 6, ...(collapsed ? { position: "absolute", right: 4, top: 14 } : {}) }}
            onMouseEnter={e => { e.currentTarget.style.color = bc; }}
            onMouseLeave={e => { e.currentTarget.style.color = "#B0B8C1"; }}
          >
            {collapsed
              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
            }
          </button>
        </div>

        {/* Nav items */}
        <nav style={{ flex: 1, padding: collapsed ? "10px 8px" : "10px 10px", display: "flex", flexDirection: "column", gap: 2 }}>
          {visibleNav.map(({ path, label, icon: Icon, badge }) => {
            const active = path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);
            const isComms = path === "/communications";
            return (
              <NavTooltip key={path} label={collapsed ? label : ""}>
                <NavLink to={path} aria-label={label} style={{
                  display: "flex", alignItems: "center",
                  gap: collapsed ? 0 : 9,
                  justifyContent: collapsed ? "center" : "flex-start",
                  padding: collapsed ? "9px 0" : "8px 10px",
                  borderRadius: 10,
                  background: active ? `${bc}1A` : "transparent",
                  color: active ? bc : "#374151",
                  fontWeight: active ? 700 : 500,
                  fontSize: 12.5, textDecoration: "none",
                  transition: "all 0.13s",
                  whiteSpace: "nowrap", overflow: "hidden",
                  borderLeft: active ? `2.5px solid ${bc}` : "2.5px solid transparent",
                  boxShadow: active ? `0 1px 6px ${bc}1A` : "none",
                  position: "relative",
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = "rgba(255,255,255,0.65)"; e.currentTarget.style.color = "#1f2937"; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#374151"; } }}
                >
                  <div style={{ position: "relative", flexShrink: 0 }}>
                    <Icon size={16} strokeWidth={active ? 2 : 1.6} />
                    {isComms && pendingNotesCount > 0 && collapsed && (
                      <span style={{ position: "absolute", top: -5, right: -6, background: "#EF4444", color: "#fff", borderRadius: "50%", width: 14, height: 14, fontSize: 8, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", border: "1.5px solid #fff" }}>
                        {pendingNotesCount > 9 ? "9+" : pendingNotesCount}
                      </span>
                    )}
                  </div>
                  {!collapsed && <span style={{ overflow: "hidden", textOverflow: "ellipsis", flex: 1 }}>{label}</span>}
                  {!collapsed && isComms && pendingNotesCount > 0 && (
                    <span style={{ background: "#EF4444", color: "#fff", borderRadius: 20, padding: "1px 6px", fontSize: 10, fontWeight: 800, flexShrink: 0, marginLeft: "auto" }}>
                      {pendingNotesCount}
                    </span>
                  )}
                  {!collapsed && badge && !active && (
                    <span style={{ background: bc, color: "#fff", borderRadius: 20, padding: "1px 6px", fontSize: 9, fontWeight: 800, flexShrink: 0, letterSpacing: "0.05em" }}>
                      {badge}
                    </span>
                  )}
                </NavLink>
              </NavTooltip>
            );
          })}
        </nav>

        {/* Sign out */}
        <div style={{ padding: collapsed ? "8px 8px 16px" : "8px 10px 16px", borderTop: "1px solid rgba(0,0,0,0.07)", flexShrink: 0 }}>
          <NavTooltip label={collapsed ? "Sign out" : ""}>
            <button onClick={() => { useStore.getState().resetStore(); supabase.auth.signOut(); }}
              style={{
                display: "flex", alignItems: "center",
                gap: collapsed ? 0 : 9,
                justifyContent: collapsed ? "center" : "flex-start",
                width: "100%", padding: collapsed ? "9px 0" : "8px 10px", borderRadius: 10,
                background: "transparent", border: "none",
                color: C.silver, cursor: "pointer",
                fontSize: 12.5, fontWeight: 500, transition: "all 0.13s",
                whiteSpace: "nowrap", overflow: "hidden", fontFamily: "inherit",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.65)"; e.currentTarget.style.color = "#DC2626"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#9CA3AF"; }}
            >
              <LogOut size={15} strokeWidth={1.6} style={{ flexShrink: 0 }} />
              {!collapsed && <span>Sign out</span>}
            </button>
          </NavTooltip>
        </div>
      </div>

      {/* ── Right column: topbar + scrollable content ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, marginLeft: W, transition: "margin-left 0.22s cubic-bezier(0.4,0,0.2,1)", height: "100vh", overflow: "hidden" }}>
        <DesktopTopbar userEmail={userEmail} />

        <div id="main-scroll" style={{ flex: 1, overflowY: "auto", overflowX: "clip", background: "#FFFFFF", overflowAnchor: "none" }}>
          <div style={{ padding: isTablet ? "18px 20px" : "24px 28px", maxWidth: 1400, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>
            <PageBanner location={location} />
            {children}
          </div>
          <div style={{ textAlign: "center", padding: "12px 20px 16px", borderTop: "1px solid #E2E5EA" }}>
            <span style={{ fontSize: 10, color: "#CBD5E1", fontWeight: 500 }}>
              © {new Date().getFullYear()} MDG Accounting. All rights reserved.
            </span>
          </div>
        </div>
      </div>

      <FloatingTimerWidget />
      {!isTeamMember && <Suspense fallback={null}><Cloey /></Suspense>}
    </div>
  );
}
